import React, { useState, useEffect, useRef } from "react";
import {
  Lock,
  LayoutDashboard,
  Tv,
  Globe,
  Settings as SettingsIcon,
  Plus,
  Edit2,
  Trash2,
  Save,
  CheckCircle2,
  ChevronRight,
  Eye,
  Heart,
  FileText,
  Sparkles,
  StopCircle,
  Loader2,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Layers,
  Search,
  Download,
  User,
  ShieldAlert,
  RotateCw,
  Zap,
  Bot,
  Send,
  Radio,
  Share2,
  UploadCloud,
  MessageSquare,
  Check,
  X
} from "lucide-react";
import { Video, Scraper, Settings, Page, getProxiedThumbnailUrl } from "../types";

interface AdminPanelProps {
  isAdmin: boolean;
  onLogin: (password: string) => Promise<boolean>;
  videos: Video[];
  scrapers: Scraper[];
  settings: Settings;
  pages: Page[];
  onAddVideo: (video: Omit<Video, "id" | "views" | "likes" | "createdAt">) => Promise<any>;
  onEditVideo: (id: string, video: Partial<Video>) => Promise<void>;
  onDeleteVideo: (id: string) => Promise<void>;
  onDeleteAllVideos?: () => Promise<void>;
  onBulkDeleteVideos?: (keyword: string) => Promise<{ success: boolean; message: string; deletedCount: number }>;
  onDeleteMultipleVideos?: (ids: string[]) => Promise<{ success: boolean; message: string; deletedCount: number }>;
  onRefreshVideos?: () => Promise<void>;
  onAddScraper: (scraper: Omit<Scraper, "id" | "clicks">) => Promise<void>;
  onEditScraper: (id: string, scraper: Partial<Scraper>) => Promise<void>;
  onDeleteScraper: (id: string) => Promise<void>;
  onUpdateSettings: (settings: Partial<Settings> & { adminPassword?: string }) => Promise<void>;
  onUpdatePage: (id: string, page: Partial<Page>) => Promise<void>;
  onRenameCategory?: (oldName: string, newName: string) => Promise<{ success: boolean; message: string }>;
  onDeleteCategory?: (category: string, deleteVideos: boolean) => Promise<{ success: boolean; message: string }>;
}

export default function AdminPanel({
  isAdmin,
  onLogin,
  videos,
  scrapers,
  settings,
  pages,
  onAddVideo,
  onEditVideo,
  onDeleteVideo,
  onDeleteAllVideos,
  onBulkDeleteVideos,
  onDeleteMultipleVideos,
  onRefreshVideos,
  onAddScraper,
  onEditScraper,
  onDeleteScraper,
  onUpdateSettings,
  onUpdatePage,
  onRenameCategory,
  onDeleteCategory,
}: AdminPanelProps) {
  // Authentication local states
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Nav tab local states
  const [activeAdminTab, setActiveAdminTab] = useState<"dashboard" | "videos" | "scrapers" | "pages" | "settings" | "ai-crawler" | "visitors" | "telegram">("ai-crawler");

  // Visitors / User Activity States
  const [visitors, setVisitors] = useState<any[]>([]);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [visitorSearch, setVisitorSearch] = useState("");
  const [visitorFilter, setVisitorFilter] = useState<"all" | "blocked" | "active">("all");

  const fetchVisitors = async () => {
    setLoadingVisitors(true);
    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      const res = await fetch("/api/admin/visitors", {
        headers: {
          "Authorization": `Bearer ${savedToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setVisitors(data.visitors || []);
      } else {
        triggerStatus("Failed to load visitor directory.", "error");
      }
    } catch (e) {
      triggerStatus("An error occurred loading visitors.", "error");
    } finally {
      setLoadingVisitors(false);
    }
  };

  useEffect(() => {
    if (activeAdminTab === "visitors" && localStorage.getItem("admin_secret_token")) {
      fetchVisitors();
    }
  }, [activeAdminTab]);

  const handleBlockVisitor = async (visitorId: string, ip: string) => {
    showConfirm(
      "Block User?",
      `Are you sure you want to suspend access for this visitor (IP: ${ip || "N/A"})? They will not be able to load any streaming portal content.`,
      async () => {
        try {
          const savedToken = localStorage.getItem("admin_secret_token") || "";
          const res = await fetch("/api/admin/visitors/block", {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${savedToken}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ visitorId, ip })
          });
          if (res.ok) {
            const data = await res.json();
            setVisitors(data.visitors || []);
            triggerStatus("User has been successfully blocked.", "success");
          } else {
            triggerStatus("Failed to block visitor.", "error");
          }
        } catch (e) {
          triggerStatus("Error occurred while blocking.", "error");
        }
      },
      "Yes, Block Access"
    );
  };

  const handleUnblockVisitor = async (visitorId: string, ip: string) => {
    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      const res = await fetch("/api/admin/visitors/unblock", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${savedToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ visitorId, ip })
      });
      if (res.ok) {
        const data = await res.json();
        setVisitors(data.visitors || []);
        triggerStatus("User has been unblocked.", "success");
      } else {
        triggerStatus("Failed to unblock visitor.", "error");
      }
    } catch (e) {
      triggerStatus("Error occurred while unblocking.", "error");
    }
  };

  const handleDeleteVisitor = async (visitorId: string) => {
    showConfirm(
      "Remove Visitor Log?",
      "Are you sure you want to delete this visitor record? This clears their request count and history from the admin view.",
      async () => {
        try {
          const savedToken = localStorage.getItem("admin_secret_token") || "";
          const res = await fetch("/api/admin/visitors/delete", {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${savedToken}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ visitorId })
          });
          if (res.ok) {
            const data = await res.json();
            setVisitors(data.visitors || []);
            triggerStatus("Visitor log entry deleted.", "success");
          } else {
            triggerStatus("Failed to delete record.", "error");
          }
        } catch (e) {
          triggerStatus("Error deleting record.", "error");
        }
      },
      "Yes, Delete Log"
    );
  };

  // AI Crawler Local States - Target Site (StripTube Madison Flame)
  const DEFAULT_4_SITES = [
    "https://striptube.cc/performer/madison_flame"
  ];

  const [crawlUrls, setCrawlUrls] = useState<string[]>(DEFAULT_4_SITES);
  const [crawlUrl, setCrawlUrl] = useState("");
  const [deepCrawl, setDeepCrawl] = useState(true);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlLogs, setCrawlLogs] = useState<string[]>([]);
  const [crawlResults, setCrawlResults] = useState<any[]>([]);
  const [selectedCrawlVideos, setSelectedCrawlVideos] = useState<number[]>([]); // index of crawlResults
  const [crawlError, setCrawlError] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [scrapedSiteInfo, setScrapedSiteInfo] = useState<{ title: string; description: string; thumb: string } | null>(null);

  const [scrapedCount, setScrapedCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [totalQueued, setTotalQueued] = useState(0);
  const [statsByWebsite, setStatsByWebsite] = useState<Record<string, { scraped: number; duplicates: number }>>({});

  const onRefreshRef = useRef(onRefreshVideos);
  useEffect(() => {
    onRefreshRef.current = onRefreshVideos;
  }, [onRefreshVideos]);

  // Background Status Polling Effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const pollStatus = async () => {
      try {
        const savedToken = localStorage.getItem("admin_secret_token") || "";
        const response = await fetch("/api/admin/crawler/status", {
          headers: {
            "Authorization": `Bearer ${savedToken}`
          }
        });
        if (response.ok) {
          const statusData = await response.json();
          if (statusData.targetUrl && !crawlUrl) {
            setCrawlUrl(statusData.targetUrl);
          }
          setIsCrawling(statusData.status === "running");
          setCrawlLogs((prev) => {
            const next = statusData.logs || [];
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
          });
          setScrapedCount((prev) => statusData.scrapedCount !== undefined ? statusData.scrapedCount : prev);
          setDuplicateCount((prev) => statusData.duplicateCount !== undefined ? statusData.duplicateCount : prev);
          setTotalQueued(statusData.totalQueued || 0);
          if (statusData.statsByWebsite) {
            setStatsByWebsite(statusData.statsByWebsite);
          }

          if (statusData.status === "running" && onRefreshRef.current) {
            onRefreshRef.current();
          }
        }
      } catch (e) {
        // Silently ignore polling network errors (expected during dev server restarts)
      }
    };

    pollStatus();
    interval = setInterval(pollStatus, 3000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // Status message states
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState<"success" | "error">("success");

  // Video Form states
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoThumb, setVideoThumb] = useState("");
  const [videoCat, setVideoCat] = useState("Trending");
  const [videoDuration, setVideoDuration] = useState("05:00");
  const [videoDesc, setVideoDesc] = useState("");
  const [setAsOmnitrix, setSetAsOmnitrix] = useState(false);
  const [bulkDeleteKeyword, setBulkDeleteKeyword] = useState("");
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [adminVideoSearch, setAdminVideoSearch] = useState("");
  
  // Category Editing local states
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [deletingCategoryItem, setDeletingCategoryItem] = useState<string | null>(null);

  // Custom confirmation modal state for mobile-safe clicks
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void | Promise<void>;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes, Proceed",
    cancelText: "Cancel",
    onConfirm: () => {},
  });

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void | Promise<void>,
    confirmText = "Yes, Proceed",
    cancelText = "Cancel"
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText,
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        try {
          await onConfirm();
        } catch (e) {
          triggerStatus("Action failed.", "error");
        }
      },
    });
  };

  // Scraper Form states
  const [editingScraperId, setEditingScraperId] = useState<string | null>(null);
  const [scraperName, setScraperName] = useState("");
  const [scraperUrl, setScraperUrl] = useState("");
  const [scraperCat, setScraperCat] = useState("General");

  // Settings states
  const [siteName, setSiteName] = useState(settings.siteName || "");
  const [siteSlogan, setSiteSlogan] = useState(settings.siteSlogan || "");
  const [newPassword, setNewPassword] = useState("");
  const [omnitrixFeaturedVideoId, setOmnitrixFeaturedVideoId] = useState(settings.omnitrixFeaturedVideoId || "");
  const [watermarkText, setWatermarkText] = useState(settings.watermarkText || "");
  const [titleSuffix, setTitleSuffix] = useState(settings.titleSuffix || "");
  const [stripPhrases, setStripPhrases] = useState(settings.stripPhrases || "");
  const [enableTitleSuffix, setEnableTitleSuffix] = useState(settings.enableTitleSuffix !== undefined ? settings.enableTitleSuffix : true);

  // Telegram Integration States
  const [telegramBotToken, setTelegramBotToken] = useState(settings.telegramBotToken || "");
  const [telegramChatId, setTelegramChatId] = useState(settings.telegramChatId || "");
  const [telegramApiId, setTelegramApiId] = useState(settings.telegramApiId || "");
  const [telegramApiHash, setTelegramApiHash] = useState(settings.telegramApiHash || "");
  const [telegramAutoForward, setTelegramAutoForward] = useState(settings.telegramAutoForward || false);
  const [telegramBypassLimit, setTelegramBypassLimit] = useState(settings.telegramBypassLimit !== undefined ? settings.telegramBypassLimit : true);
  
  const [isTestingTelegram, setIsTestingTelegram] = useState(false);
  const [telegramTestResult, setTelegramTestResult] = useState<{ success: boolean; message: string; botName?: string } | null>(null);
  const [isBatchForwarding, setIsBatchForwarding] = useState(false);
  const [batchForwardProgress, setBatchForwardProgress] = useState<{ sent: number; total: number; currentTitle?: string } | null>(null);
  const [telegramLogs, setTelegramLogs] = useState<string[]>([]);

  // Static Pages state
  const [selectedPageId, setSelectedPageId] = useState(pages[0]?.id || "about-us");
  const [pageTitle, setPageTitle] = useState(pages[0]?.title || "");
  const [pageContent, setPageContent] = useState(pages[0]?.content || "");

  // Show temporary feedback banner
  const triggerStatus = (msg: string, type: "success" | "error" = "success") => {
    setStatusMessage(msg);
    setStatusType(type);
    setTimeout(() => setStatusMessage(""), 4000);
  };

  // Login handler
  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput) return;
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const success = await onLogin(passwordInput);
      if (!success) {
        setLoginError("Incorrect password. Please try again.");
      } else {
        setPasswordInput("");
      }
    } catch (err) {
      setLoginError("Authentication server error.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Video submit handler (Add/Edit)
  const handleVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoTitle || !videoUrl) {
      triggerStatus("Title and Video URL are required!", "error");
      return;
    }

    try {
      const payload = {
        title: videoTitle,
        videoUrl,
        thumbnailUrl: videoThumb,
        category: videoCat,
        duration: videoDuration,
        description: videoDesc,
      };

      if (editingVideoId) {
        await onEditVideo(editingVideoId, payload);
        if (setAsOmnitrix) {
          await fetch("/api/admin/settings", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": localStorage.getItem("admin_token") || "",
            },
            body: JSON.stringify({ omnitrixFeaturedVideoId: editingVideoId }),
          });
          setOmnitrixFeaturedVideoId(editingVideoId);
        }
        triggerStatus("Video successfully updated!");
      } else {
        const addedVideo = await onAddVideo(payload);
        if (setAsOmnitrix && addedVideo?.id) {
          await fetch("/api/admin/settings", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": localStorage.getItem("admin_token") || "",
            },
            body: JSON.stringify({ omnitrixFeaturedVideoId: addedVideo.id }),
          });
          setOmnitrixFeaturedVideoId(addedVideo.id);
        }
        triggerStatus("New Video successfully added!");
      }

      // Reset Form
      setEditingVideoId(null);
      setVideoTitle("");
      setVideoUrl("");
      setVideoThumb("");
      setVideoCat("Trending");
      setVideoDuration("05:00");
      setVideoDesc("");
      setSetAsOmnitrix(false);
    } catch (err) {
      triggerStatus("Failed to save video data.", "error");
    }
  };

  // Populate form for editing video
  const startEditVideo = (v: Video) => {
    setEditingVideoId(v.id);
    setVideoTitle(v.title);
    setVideoUrl(v.videoUrl);
    setVideoThumb(v.thumbnailUrl);
    setVideoCat(v.category);
    setVideoDuration(v.duration);
    setVideoDesc(v.description);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Video Delete
  const handleDeleteVideoClick = async (id: string) => {
    showConfirm(
      "Delete Video?",
      "Are you sure you want to delete this video from the system? This action is permanent and cannot be undone.",
      async () => {
        try {
          await onDeleteVideo(id);
          triggerStatus("Video deleted successfully.");
        } catch (err) {
          triggerStatus("Failed to delete video.", "error");
        }
      },
      "Yes, Delete Video"
    );
  };

  // Scraper submit (Add/Edit)
  const handleScraperSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scraperName || !scraperUrl) {
      triggerStatus("Name and Web URL are required!", "error");
      return;
    }

    try {
      const payload = {
        name: scraperName,
        url: scraperUrl,
        category: scraperCat,
      };

      if (editingScraperId) {
        await onEditScraper(editingScraperId, payload);
        triggerStatus("Web Stream Preset updated!");
      } else {
        await onAddScraper(payload);
        triggerStatus("New Web Stream Preset added!");
      }

      setEditingScraperId(null);
      setScraperName("");
      setScraperUrl("");
      setScraperCat("General");
    } catch (err) {
      triggerStatus("Failed to save scraper preset.", "error");
    }
  };

  const startEditScraper = (s: Scraper) => {
    setEditingScraperId(s.id);
    setScraperName(s.name);
    setScraperUrl(s.url);
    setScraperCat(s.category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteScraperClick = async (id: string) => {
    showConfirm(
      "Delete Preset?",
      "Are you sure you want to delete this web stream preset?",
      async () => {
        try {
          await onDeleteScraper(id);
          triggerStatus("Web Preset deleted successfully.");
        } catch (err) {
          triggerStatus("Failed to delete preset.", "error");
        }
      },
      "Yes, Delete Preset"
    );
  };

  // Page selection and edit
  const handlePageSelect = (pageId: string) => {
    setSelectedPageId(pageId);
    const p = pages.find((page) => page.id === pageId);
    if (p) {
      setPageTitle(p.title);
      setPageContent(p.content);
    }
  };

  const handlePageSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onUpdatePage(selectedPageId, { title: pageTitle, content: pageContent });
      triggerStatus("Static page layout updated successfully!");
    } catch (err) {
      triggerStatus("Failed to update page.", "error");
    }
  };

  // Save Settings
  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = { 
        siteName, 
        siteSlogan, 
        omnitrixFeaturedVideoId,
        watermarkText,
        titleSuffix,
        stripPhrases,
        enableTitleSuffix,
        telegramBotToken: telegramBotToken.trim(),
        telegramChatId: telegramChatId.trim(),
        telegramApiId: telegramApiId.trim(),
        telegramApiHash: telegramApiHash.trim(),
        telegramAutoForward,
        telegramBypassLimit
      };
      if (newPassword.trim()) {
        payload.adminPassword = newPassword.trim();
      }

      await onUpdateSettings(payload);
      triggerStatus("Configurations & Telegram settings saved successfully!", "success");
      setNewPassword("");
    } catch (err) {
      triggerStatus("Failed to update settings.", "error");
    }
  };

  // --- TELEGRAM INTEGRATION HANDLERS ---

  const handleTestTelegram = async () => {
    if (!telegramBotToken.trim() || !telegramChatId.trim()) {
      triggerStatus("Please provide both Telegram Bot Token and Chat/Group ID.", "error");
      return;
    }

    setIsTestingTelegram(true);
    setTelegramTestResult(null);

    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      const res = await fetch("/api/admin/telegram/test", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${savedToken}`
        },
        body: JSON.stringify({
          botToken: telegramBotToken.trim(),
          chatId: telegramChatId.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setTelegramTestResult({
          success: true,
          message: data.message || "Connected to Telegram channel successfully!",
          botName: data.botName
        });
        triggerStatus(`Telegram Connected: @${data.botName || 'Bot'}`, "success");
      } else {
        setTelegramTestResult({
          success: false,
          message: data.error || "Failed to reach Telegram bot or chat ID."
        });
        triggerStatus(data.error || "Telegram connection test failed.", "error");
      }
    } catch (err: any) {
      setTelegramTestResult({
        success: false,
        message: err.message || "Network error testing Telegram connection."
      });
      triggerStatus("Telegram connection test error.", "error");
    } finally {
      setIsTestingTelegram(false);
    }
  };

  const handleSendSingleToTelegram = async (v: Video) => {
    if (!telegramBotToken.trim() || !telegramChatId.trim()) {
      triggerStatus("Please configure Telegram Bot Token & Chat ID in Settings first.", "error");
      return;
    }

    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      triggerStatus(`Dispatching "${v.title.substring(0, 25)}..." to Telegram...`, "success");
      
      const res = await fetch("/api/admin/telegram/send-video", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${savedToken}`
        },
        body: JSON.stringify({
          video: v,
          botToken: telegramBotToken.trim(),
          chatId: telegramChatId.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        triggerStatus(`"${v.title.substring(0, 30)}" posted to Telegram!`, "success");
        setTelegramLogs((prev) => [
          `[${new Date().toLocaleTimeString()}] Successfully posted: "${v.title}"`,
          ...prev
        ].slice(0, 30));
      } else {
        triggerStatus(data.error || "Failed to dispatch video to Telegram.", "error");
      }
    } catch (err: any) {
      triggerStatus("Error dispatching video to Telegram.", "error");
    }
  };

  const handleBatchForwardTelegram = async (videoList: Video[]) => {
    if (!telegramBotToken.trim() || !telegramChatId.trim()) {
      triggerStatus("Please configure Telegram Bot Token & Chat ID in Settings first.", "error");
      return;
    }

    if (videoList.length === 0) {
      triggerStatus("No videos available to forward.", "error");
      return;
    }

    setIsBatchForwarding(true);
    setBatchForwardProgress({ sent: 0, total: videoList.length });
    setTelegramLogs((prev) => [
      `[${new Date().toLocaleTimeString()}] Initializing bulk forward of ${videoList.length} videos to Telegram...`,
      ...prev
    ]);

    const savedToken = localStorage.getItem("admin_secret_token") || "";

    try {
      const res = await fetch("/api/admin/telegram/send-batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${savedToken}`
        },
        body: JSON.stringify({
          videos: videoList,
          botToken: telegramBotToken.trim(),
          chatId: telegramChatId.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setBatchForwardProgress({ sent: data.sent || videoList.length, total: videoList.length });
        triggerStatus(`Batch forward complete! ${data.sent} videos posted to Telegram.`, "success");
        setTelegramLogs((prev) => [
          `[${new Date().toLocaleTimeString()}] Batch forward finished: ${data.sent}/${videoList.length} dispatched.`,
          ...prev
        ]);
      } else {
        triggerStatus(data.error || "Batch forward stopped with error.", "error");
      }
    } catch (err: any) {
      triggerStatus("Network error during batch forward.", "error");
    } finally {
      setIsBatchForwarding(false);
    }
  };

  // --- AI WEB CRAWLER HANDLERS ---

  const handleUrlChange = (index: number, value: string) => {
    setCrawlUrls((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleLoad4Presets = () => {
    setCrawlUrls([...DEFAULT_4_SITES]);
    triggerStatus("Loaded target website preset (StripTube Madison Flame)!", "success");
  };

  const handleClearAllSlots = () => {
    setCrawlUrls(Array(4).fill(""));
    setCrawlUrl("");
    triggerStatus("Cleared all website link slots.", "success");
  };

  const handleStartCrawl = async (e: React.FormEvent) => {
    e.preventDefault();
    const validUrls = crawlUrls.map(u => u.trim()).filter(Boolean);
    if (validUrls.length === 0 && crawlUrl.trim()) {
      validUrls.push(crawlUrl.trim());
    }

    if (validUrls.length === 0) {
      triggerStatus("Please specify at least 1 target website URL to crawl!", "error");
      return;
    }

    setIsCrawling(true);
    setCrawlError("");

    setCrawlLogs([
      `[SYSTEM] Initializing 4-Site Parallel AI Auto Crawler across ${validUrls.length} website link(s)...`,
      ...validUrls.map((u, i) => `[SITE #${i + 1}] Target URL: ${u}`),
      "[SYSTEM] Launching multi-site concurrent crawler threads (Fast Ingestion)..."
    ]);

    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      const response = await fetch("/api/admin/crawler/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${savedToken}`,
        },
        body: JSON.stringify({
          urls: validUrls,
          url: validUrls[0],
          deepCrawl: !!deepCrawl
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      triggerStatus(`Background AI Auto Crawler started across ${validUrls.length} site(s)!`, "success");
    } catch (err: any) {
      console.error("AI Crawler failed:", err);
      setCrawlError(err.message || "An unexpected error occurred.");
      setCrawlLogs(prev => [
        ...prev,
        `[CRITICAL ERROR] Failed to start crawler: ${err.message}`
      ]);
      triggerStatus("Failed to trigger background crawler", "error");
      setIsCrawling(false);
    }
  };

  const handleStopCrawl = async () => {
    try {
      const savedToken = localStorage.getItem("admin_secret_token") || "";
      const response = await fetch("/api/admin/crawler/stop", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${savedToken}`,
        }
      });
      if (response.ok) {
        triggerStatus("Crawl job stopped successfully.", "success");
      }
    } catch (err) {
      console.error("Failed to stop crawler", err);
    }
  };

  const handleImportCrawlVideos = async () => {
    if (selectedCrawlVideos.length === 0) {
      triggerStatus("No crawled videos selected for import!", "error");
      return;
    }

    setIsImporting(true);

    let successCount = 0;
    let duplicateCount = 0;
    let failCount = 0;

    for (const idx of selectedCrawlVideos) {
      const v = crawlResults[idx];
      
      // Duplicate check
      const isDuplicate = videos.some((existing) => 
        existing.videoUrl === v.videoUrl || 
        existing.title.toLowerCase().trim() === v.title.toLowerCase().trim()
      );
      
      if (isDuplicate) {
        duplicateCount++;
        continue;
      }

      try {
        await onAddVideo({
          title: v.title,
          videoUrl: v.videoUrl,
          thumbnailUrl: v.thumbnailUrl,
          category: v.category,
          duration: v.duration,
          description: v.description
        });
        successCount++;
      } catch (e) {
        console.error("Failed to import video:", v, e);
        failCount++;
      }
    }

    setIsImporting(false);
    
    if (successCount > 0) {
      triggerStatus(`Successfully published ${successCount} new videos!`);
    } else if (duplicateCount > 0) {
      triggerStatus(`Skipped ${duplicateCount} duplicate videos.`, "success");
    } else {
      triggerStatus("Import completed.", "success");
    }
    
    setCrawlResults([]);
    setSelectedCrawlVideos([]);
    setCrawlUrl("");
    setScrapedSiteInfo(null);
    setActiveAdminTab("videos");
  };

  const handleEditResultField = (index: number, field: string, value: string) => {
    setCrawlResults(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleToggleSelectVideo = (index: number) => {
    setSelectedCrawlVideos(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedCrawlVideos.length === crawlResults.length) {
      setSelectedCrawlVideos([]);
    } else {
      setSelectedCrawlVideos(crawlResults.map((_, i) => i));
    }
  };

  // Calculate Overview Dashboard Statistics
  const totalViews = React.useMemo(() => videos.reduce((acc, v) => acc + (v.views || 0), 0), [videos]);
  const totalLikes = React.useMemo(() => videos.reduce((acc, v) => acc + (v.likes || 0), 0), [videos]);
  const totalScraperClicks = React.useMemo(() => scrapers.reduce((acc, s) => acc + (s.clicks || 0), 0), [scrapers]);

  // --- RENDERING IF NOT AUTHENTICATED ---
  if (!isAdmin) {
    return (
      <div id="admin-login-stage" className="max-w-md mx-auto py-16 sm:py-24 px-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl relative">
          
          {/* Neon locks ornament */}
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-600/10 border border-rose-500/30 flex items-center justify-center mb-6">
            <Lock className="w-6 h-6 text-rose-500 animate-pulse" />
          </div>

          <div className="text-center space-y-2 mb-8">
            <h2 className="text-xl sm:text-2xl font-black text-slate-100 tracking-tight uppercase font-sans">
              Admin Controller
            </h2>
            <p className="text-xs text-slate-400">
              Only authorized operators can customize media collections or scraper lists.
            </p>
          </div>

          <form onSubmit={handleAuthSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 font-mono">
                Master Security Key
              </label>
              <input
                id="admin-password-field"
                type="password"
                placeholder="Enter admin password..."
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-4 py-2.5 text-slate-200 text-sm focus:outline-none transition-all placeholder-slate-700 font-mono tracking-widest"
              />
            </div>

            {loginError && (
              <p id="login-error-msg" className="text-xs font-bold text-rose-500 text-center font-mono animate-bounce">
                {loginError}
              </p>
            )}

            <button
              id="admin-login-submit"
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white font-bold py-3 px-4 rounded-lg text-sm uppercase tracking-wider transition-all shadow-md shadow-rose-600/20 flex items-center justify-center gap-2 cursor-pointer"
            >
              {isLoggingIn ? "Authenticating..." : "Unlock Dashboard"}
            </button>
          </form>

          <p className="text-[10px] text-center text-slate-600 font-mono mt-6">
            Tip: The default password is "admin". Change it anytime in settings.
          </p>
        </div>
      </div>
    );
  }

  // --- RENDERING MAIN ADMIN CONTROL COCKPIT ---
  const [adminPage, setAdminPage] = useState(1);
  const itemsPerPage = 30;

  useEffect(() => {
    setAdminPage(1);
  }, [adminVideoSearch]);

  const filteredAdminVideos = React.useMemo(() => {
    const q = adminVideoSearch.toLowerCase();
    return videos.filter((v) => {
      return (
        (v.title || "").toLowerCase().includes(q) ||
        (v.category || "").toLowerCase().includes(q) ||
        (v.videoUrl || "").toLowerCase().includes(q)
      );
    });
  }, [videos, adminVideoSearch]);

  const paginatedAdminVideos = React.useMemo(() => {
    const startIndex = (adminPage - 1) * itemsPerPage;
    return filteredAdminVideos.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAdminVideos, adminPage]);

  const domainGroupedStats = React.useMemo(() => {
    const stats = videos.reduce((acc: Record<string, number>, v) => {
      if (v.videoUrl) {
        try {
          const parsed = new URL(v.videoUrl);
          const dom = parsed.hostname.replace(/^(www|m|mobile|video|cdn|embed)\./, "").toLowerCase();
          acc[dom] = (acc[dom] || 0) + 1;
        } catch(e) {
          acc["custom_links"] = (acc["custom_links"] || 0) + 1;
        }
      }
      return acc;
    }, {});
    return Object.entries(stats);
  }, [videos]);

  const bulkDeleteCount = React.useMemo(() => {
    if (!bulkDeleteKeyword.trim()) return 0;
    const kw = bulkDeleteKeyword.toLowerCase();
    return videos.filter(v => 
      (v.videoUrl && v.videoUrl.toLowerCase().includes(kw)) ||
      (v.thumbnailUrl && v.thumbnailUrl.toLowerCase().includes(kw)) ||
      (v.title && v.title.toLowerCase().includes(kw))
    ).length;
  }, [videos, bulkDeleteKeyword]);

  const isAllSelected = filteredAdminVideos.length > 0 && filteredAdminVideos.every((v) => selectedVideoIds.includes(v.id));

  const handleAdminSelectAllToggle = () => {
    if (isAllSelected) {
      const filteredIds = filteredAdminVideos.map((v) => v.id);
      setSelectedVideoIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      const filteredIds = filteredAdminVideos.map((v) => v.id);
      setSelectedVideoIds((prev) => {
        const union = new Set([...prev, ...filteredIds]);
        return Array.from(union);
      });
    }
  };

  const handleToggleSelectAdminVideo = (id: string) => {
    setSelectedVideoIds((prev) =>
      prev.includes(id) ? prev.filter((vId) => vId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedVideoIds.length === 0) return;
    showConfirm(
      "Delete Selected Videos?",
      `Are you sure you want to delete the ${selectedVideoIds.length} selected videos from the live library? This action is permanent and cannot be undone.`,
      async () => {
        try {
          if (onDeleteMultipleVideos) {
            const res = await onDeleteMultipleVideos(selectedVideoIds);
            triggerStatus(res?.message || `${selectedVideoIds.length} videos deleted successfully!`, "success");
            setSelectedVideoIds([]);
          } else {
            const savedToken = localStorage.getItem("admin_secret_token") || "";
            const res = await fetch("/api/admin/videos/delete-multiple", {
              method: "POST",
              headers: {
                "Authorization": `Bearer ${savedToken}`,
                "Content-Type": "application/json"
              },
              body: JSON.stringify({ ids: selectedVideoIds })
            });
            if (res.ok) {
              const data = await res.json();
              triggerStatus(data.message || "Selected videos deleted.", "success");
              setSelectedVideoIds([]);
              if (onRefreshVideos) await onRefreshVideos();
            } else {
              triggerStatus("Failed to delete selected videos.", "error");
            }
          }
        } catch (err) {
          triggerStatus("An error occurred during deletion.", "error");
        }
      },
      "Yes, Delete Selected"
    );
  };

  return (
    <div id="admin-cockpit" className="space-y-6">
      
      {/* Alert Feedbacks */}
      {statusMessage && (
        <div
          id="admin-status-toast"
          className={`fixed bottom-4 right-4 z-50 px-5 py-3 rounded-xl shadow-2xl border flex items-center gap-2.5 font-sans text-xs sm:text-sm animate-bounce ${
            statusType === "success"
              ? "bg-emerald-950 border-emerald-500 text-emerald-200"
              : "bg-rose-950 border-rose-500 text-rose-200"
          }`}
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span>{statusMessage}</span>
        </div>
      )}

      {/* Cockpit layout Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-100 uppercase tracking-tight font-sans">
            AI Crawler Cockpit
          </h2>
          <p className="text-xs sm:text-sm text-slate-400">
            Paste a video link, let the AI find all related videos, categories, and tags, and instantly import them.
          </p>
        </div>

        {/* Console Side Tabs */}
        <div className="flex items-center gap-1 bg-slate-900/50 p-1 rounded-xl border border-slate-800 overflow-x-auto max-w-full">
          {[
            { id: "dashboard", label: "Dashboard 📊", icon: LayoutDashboard },
            { id: "ai-crawler", label: "AI Auto-Crawler ⚡", icon: Sparkles },
            { id: "videos", label: "Manage Videos 📂", icon: Tv },
            { id: "scrapers", label: "Link Sources / Categories 🔗", icon: Globe },
            { id: "pages", label: "Custom Pages 📄", icon: FileText },
            { id: "settings", label: "Branding & Settings ⚙️", icon: SettingsIcon },
            { id: "visitors", label: "Users & Block List 👥", icon: User },
          ].map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeAdminTab === tab.id;
            return (
              <button
                id={`admin-tab-btn-${tab.id}`}
                key={tab.id}
                onClick={() => {
                  setActiveAdminTab(tab.id as any);
                  setEditingVideoId(null);
                  setEditingScraperId(null);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  isSelected
                    ? "bg-rose-600 text-white shadow-md shadow-rose-600/10"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- TAB 1: OVERVIEW METRICS DASHBOARD --- */}
      {activeAdminTab === "dashboard" && (
        <div id="overview-panel" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl">
              <span className="text-[10px] font-black tracking-widest text-slate-400 font-mono uppercase block">
                Total Video nodes
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-slate-100 font-sans">{videos.length}</span>
                <span className="text-xs text-emerald-400 font-mono">Live Nodes</span>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl">
              <span className="text-[10px] font-black tracking-widest text-slate-400 font-mono uppercase block">
                Aggregated Portal Views
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-slate-100 font-sans">{totalViews.toLocaleString()}</span>
                <span className="text-xs text-rose-500 font-mono">Hits recorded</span>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl">
              <span className="text-[10px] font-black tracking-widest text-slate-400 font-mono uppercase block">
                Portal Likes
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-rose-500 font-sans">{totalLikes.toLocaleString()}</span>
                <span className="text-xs text-slate-400 font-mono">Appreciations</span>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl">
              <span className="text-[10px] font-black tracking-widest text-slate-400 font-mono uppercase block">
                Proxy Stream Loads
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-indigo-400 font-sans">{totalScraperClicks}</span>
                <span className="text-xs text-slate-400 font-mono">Tunnel accesses</span>
              </div>
            </div>

          </div>

          <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-6">
            <h3 className="text-base font-bold text-slate-200 mb-4 font-sans uppercase">Quick System Check</h3>
            <div className="space-y-3 text-xs sm:text-sm font-mono text-slate-400">
              <div className="flex justify-between py-2 border-b border-slate-800/60">
                <span>Database File Status</span>
                <span className="text-emerald-400 font-bold">CONNECTED (db.json)</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-800/60">
                <span>Security Engine</span>
                <span className="text-amber-500 font-bold">PASS-CHECK ENABLED</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-800/60">
                <span>Scraper Iframe Sandbox Proxy</span>
                <span className="text-indigo-400 font-bold">TUNNEL CO-PASS (ACTIVE)</span>
              </div>
              <div className="flex justify-between py-2">
                <span>Default Landing Node</span>
                <span className="text-slate-300 truncate max-w-xs">{videos[0]?.title || "None"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- TAB 2: VIDEOS MANAGER (ADD / EDIT / DELETE) --- */}
      {activeAdminTab === "videos" && (
        <div id="video-manager-panel" className="space-y-8">
          
          {/* Dynamic Video Add/Edit Input Form */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <h3 className="text-md sm:text-lg font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
              <Plus className="w-5 h-5 text-rose-500" />
              {editingVideoId ? "Edit Video Node Parameters" : "Add New Video Node"}
            </h3>

            <form onSubmit={handleVideoSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Video Title</label>
                <input
                  id="v-form-title"
                  type="text"
                  placeholder="e.g. Action Packed Forest Escape [Full Stream]..."
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Video Stream URL (MP4 / YouTube embed)</label>
                <input
                  id="v-form-url"
                  type="text"
                  placeholder="Paste direct MP4 or YouTube web URL link..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Thumbnail Card Image URL</label>
                <input
                  id="v-form-thumb"
                  type="text"
                  placeholder="Leave empty for generic background..."
                  value={videoThumb}
                  onChange={(e) => setVideoThumb(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Category</label>
                  <select
                    id="v-form-cat"
                    value={videoCat}
                    onChange={(e) => setVideoCat(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                  >
                    <option value="Trending">Trending</option>
                    <option value="Hot">Hot</option>
                    <option value="Popular">Popular</option>
                    <option value="Action">Action</option>
                    <option value="Drama">Drama</option>
                    <option value="Nature">Nature</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Duration Stamp</label>
                  <input
                    id="v-form-duration"
                    type="text"
                    placeholder="e.g. 12:45"
                    value={videoDuration}
                    onChange={(e) => setVideoDuration(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Description / Synopsis</label>
                <textarea
                  id="v-form-desc"
                  rows={3}
                  placeholder="Write video synopses or tags here..."
                  value={videoDesc}
                  onChange={(e) => setVideoDesc(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div className="md:col-span-2 flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="v-form-omnitrix"
                    checked={setAsOmnitrix}
                    onChange={(e) => setSetAsOmnitrix(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-rose-500 focus:ring-rose-500/20"
                  />
                  <label htmlFor="v-form-omnitrix" className="text-xs font-bold text-slate-300 cursor-pointer">
                    Set as Omnitrix Featured Video
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  {editingVideoId && (
                    <button
                      id="cancel-v-edit"
                      type="button"
                      onClick={() => {
                        setEditingVideoId(null);
                        setVideoTitle("");
                        setVideoUrl("");
                        setVideoThumb("");
                        setVideoCat("Trending");
                        setVideoDuration("05:00");
                        setVideoDesc("");
                        setSetAsOmnitrix(false);
                      }}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold cursor-pointer"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    id="submit-v-form"
                    type="submit"
                    className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/15"
                  >
                    <Save className="w-4 h-4" />
                    {editingVideoId ? "Update Video Node" : "Publish Video Node"}
                  </button>
                </div>
              </div>

            </form>
          </div>

          {/* Video Nodes Management Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest font-mono">
                Existing Video Inventory ({videos.length})
              </h3>
              {videos.length > 0 && (
                <button
                  id="delete-all-videos-btn"
                  type="button"
                  onClick={async () => {
                    showConfirm(
                      "Delete ALL Videos?",
                      "Are you absolutely sure you want to delete ALL videos from the website? This cannot be undone and will wipe the library completely.",
                      async () => {
                        try {
                          if (onDeleteAllVideos) {
                            await onDeleteAllVideos();
                            triggerStatus("All videos have been deleted successfully.", "success");
                          } else {
                            const savedToken = localStorage.getItem("admin_secret_token") || "";
                            const res = await fetch("/api/admin/videos", {
                              method: "DELETE",
                              headers: { "Authorization": `Bearer ${savedToken}` }
                            });
                            if (res.ok) {
                              triggerStatus("All videos have been deleted successfully.", "success");
                            } else {
                              triggerStatus("Failed to delete all videos.", "error");
                            }
                          }
                        } catch (err) {
                          triggerStatus("An error occurred while deleting all videos.", "error");
                        }
                      },
                      "Yes, Wipe All Videos"
                    );
                  }}
                  className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-500 hover:text-white hover:bg-rose-650 bg-rose-950/20 border border-rose-500/20 hover:border-rose-500 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete All Videos
                </button>
              )}
            </div>

            {/* Bulk Delete Section */}
            {videos.length > 0 && (
              <div className="p-4 bg-rose-950/10 border border-rose-900/30 rounded-xl space-y-4">
                <div>
                  <span className="text-[10px] font-black text-rose-400 uppercase tracking-wider font-mono block">
                    Bulk Delete by Keyword or Tag
                  </span>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Enter a video keyword or tag to delete all matching videos from the library instantly.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      placeholder="e.g. striptube, clip"
                      value={bulkDeleteKeyword}
                      onChange={(e) => setBulkDeleteKeyword(e.target.value)}
                      className="flex-1 px-3 py-1.5 bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-500 rounded-lg text-xs font-mono focus:outline-none focus:border-rose-500"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!bulkDeleteKeyword.trim()) {
                          triggerStatus("Please enter a keyword first!", "error");
                          return;
                        }
                        const count = bulkDeleteCount;

                        if (count === 0) {
                          triggerStatus(`No videos found matching keyword: "${bulkDeleteKeyword}"`, "error");
                          return;
                        }

                        showConfirm(
                          "Bulk Delete Videos?",
                          `Are you sure you want to delete all ${count} videos matching keyword "${bulkDeleteKeyword}"? This will update the website instantly.`,
                          async () => {
                            try {
                              if (onBulkDeleteVideos) {
                                const res = await onBulkDeleteVideos(bulkDeleteKeyword);
                                triggerStatus(res?.message || `${count} videos deleted successfully!`, "success");
                                setBulkDeleteKeyword("");
                              } else {
                                // Fallback direct API call
                                const savedToken = localStorage.getItem("admin_secret_token") || "";
                                const res = await fetch("/api/admin/videos/bulk-delete", {
                                  method: "POST",
                                  headers: { 
                                    "Authorization": `Bearer ${savedToken}`,
                                    "Content-Type": "application/json"
                                  },
                                  body: JSON.stringify({ keyword: bulkDeleteKeyword })
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  triggerStatus(data.message || "Bulk deletion completed.", "success");
                                  setBulkDeleteKeyword("");
                                  if (onRefreshVideos) await onRefreshVideos();
                                } else {
                                  triggerStatus("Failed to run bulk deletion.", "error");
                                }
                              }
                            } catch (err) {
                              triggerStatus("An error occurred during bulk delete.", "error");
                            }
                          },
                          "Yes, Delete All"
                        );
                      }}
                      className="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-rose-600/10 cursor-pointer"
                    >
                      Delete ({bulkDeleteCount}) Videos
                    </button>
                  </div>
                </div>

                {/* Domain Grouped Deletion Panel */}
                <div className="border-t border-rose-900/30 pt-3 space-y-2">
                  <span className="text-[10px] font-black text-rose-400 uppercase tracking-wider font-mono block">
                    Delete All Videos by Original Website Link ({domainGroupedStats.length} Websites Detected)
                  </span>
                  <p className="text-[11px] text-slate-400">
                    See exactly how many videos came from each source link, and delete them from the live website instantly:
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-48 overflow-y-auto pr-1">
                    {domainGroupedStats.map(([domain, count]) => (
                      <div key={domain} className="bg-slate-950/60 border border-slate-900 rounded-lg p-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-slate-200 truncate font-mono" title={domain}>{domain}</p>
                          <p className="text-[10px] text-rose-400/80 font-semibold font-mono">{count} videos imported</p>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            showConfirm(
                              "Wipe All Domain Videos?",
                              `Are you sure you want to delete ALL ${count} videos from "${domain}"? This will update the live website immediately.`,
                              async () => {
                                try {
                                  if (onBulkDeleteVideos) {
                                    const res = await onBulkDeleteVideos(domain);
                                    triggerStatus(res?.message || `Successfully deleted all videos from ${domain}.`, "success");
                                  } else {
                                    const savedToken = localStorage.getItem("admin_secret_token") || "";
                                    const res = await fetch("/api/admin/videos/bulk-delete", {
                                      method: "POST",
                                      headers: { 
                                        "Authorization": `Bearer ${savedToken}`,
                                        "Content-Type": "application/json"
                                      },
                                      body: JSON.stringify({ keyword: domain })
                                    });
                                    if (res.ok) {
                                      triggerStatus(`Successfully deleted all videos from ${domain}.`, "success");
                                      if (onRefreshVideos) await onRefreshVideos();
                                    } else {
                                      triggerStatus("Failed to bulk delete.", "error");
                                    }
                                  }
                                } catch (err) {
                                  triggerStatus("Error in domain-level delete.", "error");
                                }
                              },
                              "Yes, Wipe Domain"
                            );
                          }}
                          className="px-2 py-1 bg-rose-950 hover:bg-rose-900 hover:text-white border border-rose-500/20 rounded text-[10px] font-bold text-rose-300 transition-colors flex-shrink-0 cursor-pointer"
                        >
                          Wipe All
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Inventory Controls: Search & Bulk Selection Actions */}
            {videos.length > 0 && (
              <div className="bg-slate-900/50 border border-slate-850 p-3 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3 font-sans text-xs">
                {/* Search / Filter input */}
                <div className="w-full sm:w-72 relative">
                  <input
                    type="text"
                    placeholder="Search inventory..."
                    value={adminVideoSearch}
                    onChange={(e) => setAdminVideoSearch(e.target.value)}
                    className="w-full pl-3 pr-8 py-1.5 bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-500 rounded-lg focus:outline-none focus:border-rose-500"
                  />
                  {adminVideoSearch && (
                    <button
                      onClick={() => setAdminVideoSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Bulk Actions & Selection Counters */}
                <div className="w-full sm:w-auto flex flex-wrap items-center justify-between sm:justify-end gap-3 flex-1">
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-slate-300 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleAdminSelectAllToggle}
                        className="w-4 h-4 rounded border-slate-800 bg-slate-950 text-rose-600 focus:ring-rose-500"
                      />
                      <span>Select All ({filteredAdminVideos.length})</span>
                    </label>
                    {selectedVideoIds.length > 0 && (
                      <span className="text-[10px] bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2 py-0.5 rounded-full font-bold font-mono">
                        {selectedVideoIds.length} Selected
                      </span>
                    )}
                  </div>

                  {selectedVideoIds.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleBatchForwardTelegram(videos.filter(v => selectedVideoIds.includes(v.id)))}
                        disabled={isBatchForwarding}
                        className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-lg font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-sky-600/10 transition-all"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>Send to Telegram ({selectedVideoIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteSelected}
                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/10 transition-all hover:scale-[1.02]"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete Selected ({selectedVideoIds.length})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detailed Table Column Headers */}
            {filteredAdminVideos.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 bg-slate-900/60 border border-slate-800 rounded-t-xl text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">
                <div className="col-span-1 flex items-center">Select</div>
                <div className="col-span-5 flex items-center">Video Title & Metadata</div>
                <div className="col-span-4 flex items-center">Full Video / Stream URL</div>
                <div className="col-span-2 flex items-center justify-end">Actions</div>
              </div>
            )}

            <div className="bg-slate-900/30 border border-slate-800 rounded-b-xl overflow-hidden divide-y divide-slate-800">
              {filteredAdminVideos.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  {videos.length === 0 
                    ? "No videos in database. Click Add above to add one." 
                    : "No matching videos found for your search."}
                </div>
              ) : (
                paginatedAdminVideos.map((v) => {
                  const isChecked = selectedVideoIds.includes(v.id);
                  return (
                    <div 
                      key={v.id} 
                      className={`p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-center transition-colors ${
                        isChecked ? 'bg-rose-950/10 hover:bg-rose-950/20' : 'hover:bg-slate-900/20'
                      }`}
                    >
                      {/* Checkbox selector column */}
                      <div className="col-span-1 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleSelectAdminVideo(v.id)}
                          className="w-4 h-4 rounded border-slate-800 bg-slate-950 text-rose-600 focus:ring-rose-500 cursor-pointer"
                        />
                        <span className="md:hidden text-[10px] text-slate-500 font-mono">Select Video</span>
                      </div>

                      {/* Video info & metadata column */}
                      <div className="col-span-1 md:col-span-5 flex items-start sm:items-center gap-3">
                        <img
                          src={getProxiedThumbnailUrl(v.thumbnailUrl, v.title, v.streamUrl)}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="w-16 h-10 object-cover rounded bg-black flex-shrink-0 border border-slate-800 shadow"
                        />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-xs sm:text-sm font-bold text-slate-200 line-clamp-2" title={v.title}>
                            {v.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10px] text-slate-500 font-mono mt-1">
                            <span>Genre: <b className="text-rose-400 font-bold">{v.category}</b></span>
                            <span>•</span>
                            <span>Views: <b className="text-slate-300">{v.views}</b></span>
                            <span>•</span>
                            <span>Likes: <b className="text-slate-300">{v.likes}</b></span>
                          </div>
                        </div>
                      </div>

                      {/* Full Video Stream URL Column */}
                      <div className="col-span-1 md:col-span-4 flex flex-col gap-1">
                        <span className="md:hidden text-[10px] text-slate-500 font-mono">Full Stream Link:</span>
                        <div className="flex items-center gap-1.5 w-full bg-slate-950 border border-slate-800 rounded px-2 py-1">
                          <input
                            type="text"
                            readOnly
                            value={v.videoUrl}
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                            className="w-full bg-transparent text-[11px] text-slate-300 font-mono focus:outline-none select-all truncate"
                            title="Click to select all & copy"
                          />
                          <a
                            href={v.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-200"
                            title="Open link in new tab"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>

                      {/* Actions Column */}
                      <div className="col-span-1 md:col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => handleSendSingleToTelegram(v)}
                          className="px-2 py-1.5 rounded-lg bg-sky-950 hover:bg-sky-900 border border-sky-500/30 text-sky-300 hover:text-white transition-colors cursor-pointer text-xs font-bold flex items-center gap-1"
                          title="Post this video directly to Telegram channel"
                        >
                          <Send className="w-3 h-3" />
                          <span className="hidden xl:inline">Telegram</span>
                        </button>
                        <button
                          id={`edit-video-btn-${v.id}`}
                          onClick={() => startEditVideo(v)}
                          className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors cursor-pointer text-xs font-bold flex items-center gap-1"
                          title="Edit video metadata"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          id={`delete-video-btn-${v.id}`}
                          onClick={() => handleDeleteVideoClick(v.id)}
                          className="px-2.5 py-1.5 rounded-lg bg-rose-950 hover:bg-rose-900 border border-rose-500/20 hover:border-rose-500/40 text-rose-300 transition-colors cursor-pointer text-xs font-bold flex items-center gap-1"
                          title="Delete video permanently"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination Controls */}
            {filteredAdminVideos.length > itemsPerPage && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/40 border border-slate-800 p-4 rounded-xl mt-3 font-sans text-xs">
                <div className="text-slate-400 font-medium">
                  Showing <span className="text-slate-200 font-bold">{Math.min(filteredAdminVideos.length, (adminPage - 1) * itemsPerPage + 1)}</span> to{" "}
                  <span className="text-slate-200 font-bold">{Math.min(filteredAdminVideos.length, adminPage * itemsPerPage)}</span> of{" "}
                  <span className="text-rose-400 font-black">{filteredAdminVideos.length}</span> videos
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={adminPage === 1}
                    onClick={() => setAdminPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-750 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer font-bold"
                  >
                    Previous
                  </button>
                  
                  {(() => {
                    const totalPages = Math.ceil(filteredAdminVideos.length / itemsPerPage);
                    const pagesToRender: number[] = [];
                    const startRange = Math.max(1, adminPage - 2);
                    const endRange = Math.min(totalPages, adminPage + 2);
                    for (let i = startRange; i <= endRange; i++) {
                      pagesToRender.push(i);
                    }
                    return (
                      <div className="flex items-center gap-1">
                        {startRange > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => setAdminPage(1)}
                              className={`w-8 h-8 rounded-lg font-mono font-bold transition-all cursor-pointer ${
                                adminPage === 1
                                  ? "bg-rose-600 text-white"
                                  : "bg-slate-950 text-slate-400 hover:bg-slate-800"
                              }`}
                            >
                              1
                            </button>
                            {startRange > 2 && <span className="text-slate-600 px-0.5">...</span>}
                          </>
                        )}
                        {pagesToRender.map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setAdminPage(p)}
                            className={`w-8 h-8 rounded-lg font-mono font-bold transition-all cursor-pointer ${
                              adminPage === p
                                ? "bg-rose-600 text-white shadow-md shadow-rose-600/10"
                                : "bg-slate-950 text-slate-400 hover:bg-slate-800"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        {endRange < totalPages && (
                          <>
                            {endRange < totalPages - 1 && <span className="text-slate-600 px-0.5">...</span>}
                            <button
                              type="button"
                              onClick={() => setAdminPage(totalPages)}
                              className={`w-8 h-8 rounded-lg font-mono font-bold transition-all cursor-pointer ${
                                adminPage === totalPages
                                  ? "bg-rose-600 text-white"
                                  : "bg-slate-950 text-slate-400 hover:bg-slate-800"
                              }`}
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  <button
                    type="button"
                    disabled={adminPage >= Math.ceil(filteredAdminVideos.length / itemsPerPage)}
                    onClick={() => setAdminPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-750 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer font-bold"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* --- TAB 3: WEB STREAMS PRESET MANAGER --- */}
      {activeAdminTab === "scrapers" && (
        <div id="scraper-manager-panel" className="space-y-8">
          
          {/* Preset add/edit input form */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <h3 className="text-md sm:text-lg font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
              <Plus className="w-5 h-5 text-indigo-500" />
              {editingScraperId ? "Edit Web Stream Preset" : "Configure New Web Stream Preset"}
            </h3>

            <form onSubmit={handleScraperSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Stream Site Name</label>
                <input
                  id="scr-form-name"
                  type="text"
                  placeholder="e.g. Wikipedia Main, BBC News..."
                  value={scraperName}
                  onChange={(e) => setScraperName(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Target HTTP URL</label>
                <input
                  id="scr-form-url"
                  type="text"
                  placeholder="e.g. https://en.wikipedia.org"
                  value={scraperUrl}
                  onChange={(e) => setScraperUrl(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Stream Genre / Category</label>
                <input
                  id="scr-form-cat"
                  type="text"
                  placeholder="e.g. News, Science, Social"
                  value={scraperCat}
                  onChange={(e) => setScraperCat(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div className="md:col-span-2 flex items-center gap-2 pt-2 justify-end">
                {editingScraperId && (
                  <button
                    id="cancel-scr-edit"
                    type="button"
                    onClick={() => {
                      setEditingScraperId(null);
                      setScraperName("");
                      setScraperUrl("");
                      setScraperCat("General");
                    }}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold cursor-pointer"
                  >
                    Cancel
                  </button>
                )}
                <button
                  id="submit-scr-form"
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-indigo-600/15"
                >
                  <Save className="w-4 h-4" />
                  {editingScraperId ? "Save Changes" : "Deploy Web Stream"}
                </button>
              </div>

            </form>
          </div>

          {/* Scrapers List Table */}
          <div className="space-y-3">
            <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest font-mono">
              Configured Web Stream Presets ({scrapers.length})
            </h3>

            <div className="bg-slate-900/30 border border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-800">
              {scrapers.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No web streams configured yet. Create one above!</div>
              ) : (
                scrapers.map((s) => (
                  <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-900/20 transition-colors">
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-slate-200">{s.name}</h4>
                      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-500 font-mono mt-0.5">
                        <span>Genre: <b className="text-indigo-400">{s.category}</b></span>
                        <span>Connections: <b className="text-slate-300">{s.clicks}</b></span>
                        <span className="text-slate-400">{s.url}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 self-end sm:self-center">
                      <button
                        id={`edit-scraper-btn-${s.id}`}
                        onClick={() => startEditScraper(s)}
                        className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer font-sans text-xs"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        id={`delete-scraper-btn-${s.id}`}
                        onClick={() => handleDeleteScraperClick(s.id)}
                        className="p-1.5 rounded bg-slate-800 hover:bg-rose-950 hover:text-rose-400 text-slate-300 transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Active Categories Manager & Bulk Renamer */}
          <div className="space-y-4 pt-6 border-t border-slate-800">
            <div>
              <h3 className="text-sm font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
                <Layers className="w-5 h-5 text-emerald-500" />
                Category Manager & Navigation Controller
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Rename any active category or stream genre below. This will instantly change the name on the live home page's navigation tabs for normal users and update all videos/stream presets in that category.
              </p>
            </div>

            <div className="bg-slate-900/30 border border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-800">
              {(() => {
                const uniqueCats = Array.from(new Set([
                  ...videos.map(v => v.category || "Trending"),
                  ...scrapers.map(s => s.category || "General")
                ])).filter(cat => cat && cat.toLowerCase() !== "shorts" && cat.toLowerCase() !== "short");

                if (uniqueCats.length === 0) {
                  return <div className="p-6 text-center text-slate-500 text-xs">No active video categories found.</div>;
                }

                return uniqueCats.map((cat) => {
                  const videoCount = videos.filter(v => v.category === cat).length;
                  const scraperCount = scrapers.filter(s => s.category === cat).length;
                  const isEditingThis = editingCategory === cat;
                  const isDeletingThis = deletingCategoryItem === cat;

                  return (
                    <div key={cat} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-900/10 transition-colors">
                      <div className="min-w-0 flex-1">
                        {isEditingThis ? (
                          <div className="flex items-center gap-2 max-w-md">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="Enter new category name..."
                              className="flex-1 px-2.5 py-1.5 bg-slate-950 border border-slate-800 text-slate-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!newCategoryName.trim()) {
                                  triggerStatus("New category name cannot be empty!", "error");
                                  return;
                                }
                                if (newCategoryName.trim() === cat) {
                                  setEditingCategory(null);
                                  return;
                                }
                                try {
                                  if (onRenameCategory) {
                                    const res = await onRenameCategory(cat, newCategoryName.trim());
                                    triggerStatus(res?.message || `Successfully renamed category to "${newCategoryName.trim()}"`, "success");
                                    setEditingCategory(null);
                                    setNewCategoryName("");
                                    if (onRefreshVideos) await onRefreshVideos();
                                  } else {
                                    triggerStatus("Category renaming is not supported by parent.", "error");
                                  }
                                } catch (e: any) {
                                  triggerStatus(e.message || "Failed to rename category.", "error");
                                }
                              }}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-bold cursor-pointer"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingCategory(null);
                                setNewCategoryName("");
                              }}
                              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs font-bold cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : isDeletingThis ? (
                          <div className="bg-rose-950/20 border border-rose-500/20 rounded-xl p-3 space-y-2 max-w-xl w-full">
                            <p className="text-xs font-bold text-rose-300 flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-rose-400" />
                              Delete Options for: "{cat}"
                            </p>
                            <p className="text-[10px] text-slate-400 leading-relaxed">
                              How would you like to handle the {videoCount} videos assigned to this tag? This action updates the live website immediately.
                            </p>
                            <div className="flex flex-wrap gap-2 pt-1">
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    if (onDeleteCategory) {
                                      const res = await onDeleteCategory(cat, true);
                                      triggerStatus(res?.message || `Successfully deleted category "${cat}" and all associated videos.`, "success");
                                      setDeletingCategoryItem(null);
                                      if (onRefreshVideos) await onRefreshVideos();
                                    }
                                  } catch (e: any) {
                                    triggerStatus(e.message || "Failed to delete category.", "error");
                                  }
                                }}
                                className="px-2.5 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded text-[10px] font-bold cursor-pointer transition-colors"
                              >
                                Wipe Category & All {videoCount} Videos
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    if (onDeleteCategory) {
                                      const res = await onDeleteCategory(cat, false);
                                      triggerStatus(res?.message || `Successfully removed category "${cat}". Videos moved to Trending.`, "success");
                                      setDeletingCategoryItem(null);
                                      if (onRefreshVideos) await onRefreshVideos();
                                    }
                                  } catch (e: any) {
                                    triggerStatus(e.message || "Failed to remove category.", "error");
                                  }
                                }}
                                className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-[10px] font-bold cursor-pointer transition-colors"
                              >
                                Remove Category Tag Only (Move Videos to Trending)
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingCategoryItem(null)}
                                className="px-2.5 py-1.5 bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 rounded text-[10px] font-medium cursor-pointer transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <span className="text-xs sm:text-sm font-bold text-slate-200 px-2 py-0.5 bg-slate-950 border border-slate-850 rounded-md inline-block">
                              {cat}
                            </span>
                            <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono mt-1.5">
                              <span>Assigned Videos: <b className="text-rose-400 font-bold">{videoCount}</b></span>
                              <span>•</span>
                              <span>Assigned Web Stream Presets: <b className="text-indigo-400 font-bold">{scraperCount}</b></span>
                            </div>
                          </div>
                        )}
                      </div>

                      {!isEditingThis && !isDeletingThis && (
                        <div className="flex items-center gap-2 self-end sm:self-center">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCategory(cat);
                              setNewCategoryName(cat);
                              setDeletingCategoryItem(null);
                            }}
                            className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Edit2 className="w-3 h-3 text-emerald-400" />
                            <span>Rename</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeletingCategoryItem(cat);
                              setEditingCategory(null);
                            }}
                            className="px-2.5 py-1.5 bg-rose-950/60 hover:bg-rose-900 border border-rose-500/20 text-rose-300 rounded text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Trash2 className="w-3 h-3 text-rose-400" />
                            <span>Remove Category Tag</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

        </div>
      )}

      {/* --- TAB 4: STATIC PAGES MANAGER (ABOUT / DISCLAIMER) --- */}
      {activeAdminTab === "pages" && (
        <div id="pages-manager-panel" className="space-y-6">
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <h3 className="text-md sm:text-lg font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Dynamic Pages Publisher
            </h3>

            {/* Selection row */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {pages.map((page) => (
                <button
                  id={`page-select-btn-${page.id}`}
                  key={page.id}
                  onClick={() => handlePageSelect(page.id)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold cursor-pointer border ${
                    selectedPageId === page.id
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {page.title}
                </button>
              ))}
            </div>

            <form onSubmit={handlePageSave} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Page Title / Tab Heading</label>
                <input
                  id="page-form-title"
                  type="text"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">HTML Rich Content markup</label>
                <textarea
                  id="page-form-content"
                  rows={10}
                  value={pageContent}
                  onChange={(e) => setPageContent(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                />
              </div>

              <div className="flex justify-end">
                <button
                  id="submit-page-form"
                  type="submit"
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-amber-500/10"
                >
                  <Save className="w-4 h-4" />
                  Save HTML Page layout
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- TAB 5: GENERAL SETTINGS (BRANDING & SECURITY KEYS) --- */}
      {activeAdminTab === "settings" && (
        <div id="settings-panel" className="max-w-2xl">
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <h3 className="text-md sm:text-lg font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-rose-500" />
              General Site Branding & Admin Keys
            </h3>

            <form onSubmit={handleSettingsSubmit} className="space-y-4">
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Website Brand Name</label>
                <input
                  id="settings-form-name"
                  type="text"
                  placeholder="e.g. VIRAL STREAM"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none uppercase font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Branding slogan / Banner Slogan</label>
                <input
                  id="settings-form-slogan"
                  type="text"
                  placeholder="e.g. Premium Unlimited Video Portals..."
                  value={siteSlogan}
                  onChange={(e) => setSiteSlogan(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Omnitrix Featured Video</label>
                <select
                  value={omnitrixFeaturedVideoId}
                  onChange={(e) => setOmnitrixFeaturedVideoId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none"
                >
                  <option value="">-- No Featured Video (Default Portal) --</option>
                  {videos.map(v => (
                    <option key={v.id} value={v.id}>{v.title}</option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  If selected, this video will play immediately when the user clicks the Omnitrix.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1 font-mono">Custom Video Watermark</label>
                <input
                  id="settings-form-watermark"
                  type="text"
                  placeholder="e.g. VIRAL STREAM (Leave empty to use Brand Name)"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-semibold"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Configures the floating text shown permanently inside the customized video player.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1 font-mono">Auto Title Brand Suffix</label>
                <input
                  id="settings-form-suffix"
                  type="text"
                  placeholder="e.g. VIRAL STREAM (Leave empty to use Brand Name)"
                  value={titleSuffix}
                  onChange={(e) => setTitleSuffix(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-semibold"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  The website name appended to every video title when returning data (e.g. Video Title | VIRAL STREAM).
                </span>
              </div>

              <div className="flex items-center gap-3 bg-slate-950/55 p-3 rounded-xl border border-slate-800/60">
                <input
                  id="settings-form-enable-suffix"
                  type="checkbox"
                  checked={enableTitleSuffix}
                  onChange={(e) => setEnableTitleSuffix(e.target.checked)}
                  className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 bg-slate-900 border-slate-800 cursor-pointer animate-none"
                />
                <div>
                  <label htmlFor="settings-form-enable-suffix" className="block text-xs font-bold text-slate-300 uppercase tracking-wider cursor-pointer font-mono">Enable Auto Title Suffix</label>
                  <span className="text-[10px] text-slate-500 font-mono block">
                    If enabled, every video's displayed title will automatically end with the Title Suffix configured above.
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1 font-mono">Title Phrases/Sites to Remove</label>
                <input
                  id="settings-form-strip"
                  type="text"
                  placeholder="e.g. kama-baba, kamababa, kama_baba"
                  value={stripPhrases}
                  onChange={(e) => setStripPhrases(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Comma-separated phrases/website URLs that are automatically stripped from imported or existing video titles.
                </span>
              </div>

              <div className="pt-4 border-t border-slate-800/60 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-sky-400 uppercase tracking-wider font-mono flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" />
                    Telegram Auto-Forward & Bot Settings
                  </h4>
                  <button
                    type="button"
                    onClick={handleTestTelegram}
                    disabled={isTestingTelegram}
                    className="px-2.5 py-1 bg-sky-950 hover:bg-sky-900 border border-sky-500/30 text-sky-300 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer"
                  >
                    {isTestingTelegram ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radio className="w-3 h-3" />}
                    Test Connection
                  </button>
                </div>

                {telegramTestResult && (
                  <div className={`p-3 rounded-xl text-xs font-mono border flex items-center gap-2 ${telegramTestResult.success ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300' : 'bg-rose-950/60 border-rose-500/40 text-rose-300'}`}>
                    {telegramTestResult.success ? <Check className="w-4 h-4 text-emerald-400 shrink-0" /> : <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />}
                    <span>{telegramTestResult.message} {telegramTestResult.botName ? `(@${telegramTestResult.botName})` : ''}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Telegram Bot Token</label>
                    <input
                      type="password"
                      placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                      value={telegramBotToken}
                      onChange={(e) => setTelegramBotToken(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Chat ID / Channel Username</label>
                    <input
                      type="text"
                      placeholder="@channel_name or -100123456789"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Telegram API ID (Optional - Limit Bypass)</label>
                    <input
                      type="text"
                      placeholder="e.g. 1234567"
                      value={telegramApiId}
                      onChange={(e) => setTelegramApiId(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">Telegram API Hash (Optional - Limit Bypass)</label>
                    <input
                      type="password"
                      placeholder="e.g. 0123456789abcdef0123456789abcdef"
                      value={telegramApiHash}
                      onChange={(e) => setTelegramApiHash(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-3 bg-slate-950/55 p-3 rounded-xl border border-slate-800/60">
                    <input
                      id="settings-form-tg-autoforward"
                      type="checkbox"
                      checked={telegramAutoForward}
                      onChange={(e) => setTelegramAutoForward(e.target.checked)}
                      className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-900 border-slate-800 cursor-pointer"
                    />
                    <div>
                      <label htmlFor="settings-form-tg-autoforward" className="block text-xs font-bold text-sky-300 uppercase tracking-wider cursor-pointer font-mono">
                        Auto-Forward Scraped Videos to Telegram
                      </label>
                      <span className="text-[10px] text-slate-500 font-mono block">
                        When active, every newly scraped or added video will be automatically posted to your Telegram channel.
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-slate-950/55 p-3 rounded-xl border border-slate-800/60">
                    <input
                      id="settings-form-tg-bypass"
                      type="checkbox"
                      checked={telegramBypassLimit}
                      onChange={(e) => setTelegramBypassLimit(e.target.checked)}
                      className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-900 border-slate-800 cursor-pointer"
                    />
                    <div>
                      <label htmlFor="settings-form-tg-bypass" className="block text-xs font-bold text-slate-300 uppercase tracking-wider cursor-pointer font-mono">
                        Bypass Telegram 50MB File Limit
                      </label>
                      <span className="text-[10px] text-slate-500 font-mono block">
                        Uses HTTP direct stream links and direct message payload tunneling to bypass local file size restrictions.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800/60">
                <label className="block text-xs font-bold text-rose-400 uppercase tracking-wider mb-1 font-mono">Modify Master admin password</label>
                <input
                  id="settings-form-pass"
                  type="password"
                  placeholder="Type new security password..."
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-lg px-3 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Leave this empty if you don't wish to modify the master password.
                </span>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  id="submit-settings-form"
                  type="submit"
                  className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/10"
                >
                  <Save className="w-4 h-4" />
                  Save Configurations
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* --- TAB: TELEGRAM CHANNEL & BROADCAST DISPATCHER --- */}
      {activeAdminTab === "telegram" && (
        <div id="telegram-panel" className="space-y-6 max-w-4xl animate-fade-in">
          
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-sky-950/40 via-slate-900 to-indigo-950/40 border border-sky-500/30 rounded-2xl p-5 shadow-xl relative overflow-hidden flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-xl text-sky-400 shrink-0">
                <Send className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/40 uppercase tracking-wider font-mono">
                    <span className="w-2 h-2 rounded-full bg-sky-400 animate-ping"></span>
                    Telegram Live Sync
                  </span>
                  <h4 className="text-base font-bold text-slate-100 font-sans">Telegram Channel & Group Broadcast Station</h4>
                </div>
                <p className="text-xs text-slate-300 max-w-xl">
                  Automatically forward every website video straight to your Telegram channel with full titles, direct video stream links, and thumbnails.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
              <button
                type="button"
                onClick={handleTestTelegram}
                disabled={isTestingTelegram}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-lg shadow-sky-600/20"
              >
                {isTestingTelegram ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
                <span>Test Connection</span>
              </button>
            </div>
          </div>

          {/* Test connection alert */}
          {telegramTestResult && (
            <div className={`p-4 rounded-xl text-xs font-mono border flex items-center gap-3 ${telegramTestResult.success ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300' : 'bg-rose-950/60 border-rose-500/40 text-rose-300'}`}>
              {telegramTestResult.success ? <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" /> : <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />}
              <div className="flex-1">
                <span className="font-bold">{telegramTestResult.success ? "Connection Verified: " : "Connection Error: "}</span>
                <span>{telegramTestResult.message}</span>
                {telegramTestResult.botName && <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 rounded text-[11px] text-emerald-300">Bot: @{telegramTestResult.botName}</span>}
              </div>
            </div>
          )}

          {/* Configuration Card */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2 font-mono">
              <Bot className="w-4 h-4 text-sky-400" />
              Bot & Channel Credentials
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">
                  Telegram Bot Token <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  placeholder="e.g. 7123456789:AAHkL1..."
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Obtain from @BotFather on Telegram. Add your bot as Admin in your channel.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">
                  Target Channel / Group ID <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="@your_channel_name or -100123456789"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Public username (e.g. @mychannel) or numeric ID (e.g. -10018920192).
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">
                  Telegram API ID (Optional - Limit Bypass)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 29384729"
                  value={telegramApiId}
                  onChange={(e) => setTelegramApiId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  From my.telegram.org app configuration.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 font-mono">
                  Telegram API Hash (Optional - Limit Bypass)
                </label>
                <input
                  type="password"
                  placeholder="e.g. e4d909c290d34..."
                  value={telegramApiHash}
                  onChange={(e) => setTelegramApiHash(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none font-mono"
                />
                <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                  Secures API requests for custom large-file Telegram upload protocols.
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-3 bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                <input
                  id="tab-tg-autoforward"
                  type="checkbox"
                  checked={telegramAutoForward}
                  onChange={(e) => setTelegramAutoForward(e.target.checked)}
                  className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-900 border-slate-800 cursor-pointer"
                />
                <div>
                  <label htmlFor="tab-tg-autoforward" className="block text-xs font-bold text-sky-300 uppercase tracking-wider cursor-pointer font-mono">
                    Auto-Forward New Scraped Videos
                  </label>
                  <span className="text-[10px] text-slate-500 font-mono block">
                    Automatically dispatches newly scraped videos to Telegram.
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                <input
                  id="tab-tg-bypass"
                  type="checkbox"
                  checked={telegramBypassLimit}
                  onChange={(e) => setTelegramBypassLimit(e.target.checked)}
                  className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-900 border-slate-800 cursor-pointer"
                />
                <div>
                  <label htmlFor="tab-tg-bypass" className="block text-xs font-bold text-slate-300 uppercase tracking-wider cursor-pointer font-mono">
                    Bypass Telegram 50MB Limit Protocol
                  </label>
                  <span className="text-[10px] text-slate-500 font-mono block">
                    Tunnel links directly so users can stream full videos without file size limits.
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSettingsSubmit}
                className="px-5 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-2 cursor-pointer shadow-lg shadow-sky-600/10"
              >
                <Save className="w-4 h-4" />
                Save Telegram Settings
              </button>
            </div>
          </div>

          {/* Batch Forwarding Station Card */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2 font-mono">
                  <UploadCloud className="w-4 h-4 text-emerald-400" />
                  Batch Forward All Videos ({videos.length} Total)
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Push all {videos.length} videos from your database directly to your Telegram channel with auto-flood control rate limiting.
                </p>
              </div>

              <button
                type="button"
                onClick={() => handleBatchForwardTelegram(videos)}
                disabled={isBatchForwarding || videos.length === 0}
                className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-500 hover:to-sky-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-lg shadow-emerald-600/20 shrink-0"
              >
                {isBatchForwarding ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Forwarding ({batchForwardProgress?.sent || 0}/{videos.length})...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Forward All {videos.length} Videos to Telegram</span>
                  </>
                )}
              </button>
            </div>

            {/* Progress indicator */}
            {batchForwardProgress && (
              <div className="bg-slate-950 p-3.5 rounded-xl border border-slate-800 space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-300">Dispatch Progress:</span>
                  <span className="text-emerald-400 font-bold">{batchForwardProgress.sent} / {batchForwardProgress.total} videos forwarded</span>
                </div>
                <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all duration-300"
                    style={{ width: `${Math.round((batchForwardProgress.sent / Math.max(1, batchForwardProgress.total)) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Real-time Activity Logs */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-3">
            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2 font-mono">
              <MessageSquare className="w-3.5 h-3.5 text-indigo-400" />
              Telegram Dispatch Activity Feed
            </h3>

            <div className="bg-slate-950 border border-slate-800/80 rounded-xl p-3 max-h-48 overflow-y-auto font-mono text-[11px] space-y-1">
              {telegramLogs.length === 0 ? (
                <span className="text-slate-600 italic">No dispatch activity yet. Test connection or forward a video to see live status updates here.</span>
              ) : (
                telegramLogs.map((log, i) => (
                  <div key={i} className="text-slate-300 flex items-center gap-2">
                    <span className="text-sky-400 font-bold">›</span>
                    <span>{log}</span>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      )}

      {/* --- TAB 6: AI WEB CRAWLER & INDEXER PANEL --- */}
      {activeAdminTab === "ai-crawler" && (
        <div id="ai-crawler-panel" className="space-y-6 animate-fade-in">

          {/* Permanent Kama-Baba Auto-Crawler Status Banner */}
          <div className="bg-gradient-to-r from-emerald-950/40 via-slate-900 to-indigo-950/40 border border-emerald-500/30 rounded-2xl p-5 shadow-xl relative overflow-hidden flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 shrink-0">
                <RotateCw className="w-5 h-5 animate-spin-slow" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 uppercase tracking-wider font-mono">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                    Permanent System Service Active
                  </span>
                  <h4 className="text-sm font-bold text-slate-100 font-sans">Kama-Baba.com Auto-Crawler</h4>
                </div>
                <p className="text-xs text-slate-300 max-w-xl">
                  Runs continuously in the background to automatically fetch Kama-Baba videos, resolve direct stream sources, and deduplicate directory records 24/7.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const savedToken = localStorage.getItem("admin_secret_token") || "";
                    const res = await fetch("/api/admin/dedupe", {
                      method: "POST",
                      headers: { "Authorization": `Bearer ${savedToken}` }
                    });
                    const data = await res.json();
                    const removedCount = data.removed || 0;
                    triggerStatus(`Auto-dedupe completed! Removed ${removedCount} duplicates.`, "success");
                    if (onRefreshVideos) onRefreshVideos();
                  } catch (err) {
                    triggerStatus("Failed to run deduplication.", "error");
                  }
                }}
                className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700/80 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Layers className="w-3.5 h-3.5 text-indigo-400" />
                Force Dedupe Now
              </button>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
            {/* Neon Background Accents */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-rose-600/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-600/5 rounded-full blur-3xl pointer-events-none" />

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 relative">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-rose-500 animate-pulse" />
                  <h3 className="text-lg sm:text-xl font-bold text-slate-100 font-sans uppercase tracking-tight">
                    4 AI Auto-Crawl Website Engine
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 max-w-3xl">
                  Harvest video content for StripTube Madison Flame and custom target sites. All extracted videos play seamlessly through the custom video player.
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleLoad4Presets}
                  className="px-3 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/40 rounded-xl text-xs font-bold font-mono flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                  Fill Top 4 Presets
                </button>
                <button
                  type="button"
                  onClick={handleClearAllSlots}
                  className="px-3 py-2 bg-slate-950 hover:bg-slate-800 text-slate-400 border border-slate-800 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer"
                >
                  Clear All
                </button>
              </div>
            </div>

            <form onSubmit={handleStartCrawl} className="space-y-6 relative">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider font-mono">
                    Website Links (4 Auto-Crawl Target Slots):
                  </label>
                  <span className="text-[11px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                    {crawlUrls.filter(u => u.trim()).length} of 4 Slots Active
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {crawlUrls.map((url, idx) => (
                    <div key={idx} className="relative bg-slate-950/80 border border-slate-800 focus-within:border-rose-500/80 rounded-xl p-2.5 transition-all">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                          Website Link #{idx + 1}
                        </span>
                        {url.trim() && (
                          <span className="text-[9px] font-mono text-slate-500 truncate max-w-[150px]">
                            {(() => { try { return new URL(url).hostname; } catch(e) { return "custom site"; } })()}
                          </span>
                        )}
                      </div>
                      <div className="relative flex items-center">
                        <Globe className="w-3.5 h-3.5 text-slate-500 absolute left-2.5" />
                        <input
                          type="url"
                          placeholder={`https://website-${idx + 1}.com or target link...`}
                          value={url}
                          onChange={(e) => handleUrlChange(idx, e.target.value)}
                          disabled={isCrawling}
                          className="w-full bg-transparent pl-8 pr-3 py-1 text-slate-200 text-xs focus:outline-none font-mono disabled:opacity-50"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-slate-800/80">
                <div className="flex items-center gap-2 bg-slate-950 border border-slate-800/80 rounded-xl px-4 py-2.5 select-none w-full sm:w-auto">
                  <input
                    type="checkbox"
                    id="deep-crawl-toggle"
                    checked={deepCrawl}
                    disabled={isCrawling}
                    onChange={(e) => setDeepCrawl(e.target.checked)}
                    className="w-4 h-4 accent-rose-600 rounded cursor-pointer"
                  />
                  <label htmlFor="deep-crawl-toggle" className="text-xs font-bold text-slate-300 font-sans cursor-pointer uppercase">
                    Deep Site Crawl (Extract All Videos)
                  </label>
                </div>

                {!isCrawling ? (
                  <button
                    id="start-crawler-btn"
                    type="submit"
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-rose-600 via-indigo-600 to-purple-600 hover:from-rose-500 hover:to-purple-500 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-rose-600/10 transition-all duration-300 transform hover:scale-105"
                  >
                    <Sparkles className="w-4.5 h-4.5 text-amber-300" />
                    Start 4-Site Parallel AI Auto-Crawl & Sync Custom Player
                  </button>
                ) : (
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="px-5 py-3 bg-slate-950 border border-slate-800 text-rose-400 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2.5 animate-pulse w-full justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-rose-500" />
                      Crawling 4 websites & converting video streams...
                    </div>
                    <button
                      id="stop-crawler-btn"
                      type="button"
                      onClick={handleStopCrawl}
                      className="px-5 py-3 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-rose-600/20 active:scale-95 animate-pulse"
                    >
                      <StopCircle className="w-4.5 h-4.5" />
                      STOP CRAWLER
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Crawler Diagnostics & Site Info */}
          {(isCrawling || crawlLogs.length > 0 || scrapedCount > 0 || duplicateCount > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Console Logs terminal-style view */}
              <div className="lg:col-span-8 bg-slate-950 border border-slate-800 rounded-2xl p-5 relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span className="text-xs font-mono font-bold text-slate-400 ml-1">AI_CRAWLER_LOGS.sh</span>
                  </div>
                  {isCrawling && (
                    <span className="text-[10px] font-mono text-rose-400 font-bold tracking-widest uppercase animate-pulse">
                      • Harvesting data...
                    </span>
                  )}
                </div>

                <div 
                  id="crawler-terminal-logs"
                  className="bg-black/80 font-mono text-[11px] h-64 overflow-y-auto p-4 rounded-xl space-y-1.5 scrollbar-thin scrollbar-thumb-slate-800"
                >
                  {crawlLogs.map((log, i) => {
                    let logColor = "text-green-400";
                    if (log.includes("[ERROR]") || log.includes("failed")) logColor = "text-red-400";
                    if (log.includes("[WARNING]")) logColor = "text-amber-400";
                    if (log.includes("[SYSTEM]")) logColor = "text-cyan-400";
                    if (log.includes("SUCCESS")) logColor = "text-emerald-400 font-bold";
                    if (log.includes("Gemini AI")) logColor = "text-indigo-300 font-bold";

                    return (
                      <div key={i} className={`leading-relaxed break-all ${logColor}`}>
                        <span className="text-slate-600 select-none mr-2">[{new Date().toLocaleTimeString()}]</span>
                        {log}
                      </div>
                    );
                  })}
                  {isCrawling && (
                    <div className="text-rose-400/80 animate-pulse flex items-center gap-2 mt-2">
                      <span>_</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Live Crawler Telemetry Showcase */}
              <div className="lg:col-span-4 bg-slate-900/40 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between">
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 font-mono">
                    Crawler Telemetry Status
                  </h4>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60">
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block font-mono">Discovered</span>
                        <span className="text-xl font-black text-rose-500 font-sans block mt-1">{scrapedCount}</span>
                        <span className="text-[9px] font-mono text-slate-600 block">Published live</span>
                      </div>
                      <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60">
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block font-mono">Skipped</span>
                        <span className="text-xl font-black text-slate-400 font-sans block mt-1">{duplicateCount}</span>
                        <span className="text-[9px] font-mono text-slate-600 block">Duplicates</span>
                      </div>
                    </div>

                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60 flex items-center justify-between">
                      <div>
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block font-mono">URL Scan Queue</span>
                        <span className="text-lg font-black text-indigo-400 font-sans block mt-0.5">{totalQueued}</span>
                      </div>
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                    </div>

                    <div className="p-3 bg-slate-950/60 border border-slate-800/40 rounded-xl space-y-1">
                      <div className="flex justify-between text-[10px] font-mono text-slate-500">
                        <span>Background Job</span>
                        <span className={isCrawling ? "text-emerald-400 font-bold" : "text-slate-400"}>
                          {isCrawling ? "RUNNING" : "INACTIVE"}
                        </span>
                      </div>
                      <div className="flex justify-between text-[10px] font-mono text-slate-500">
                        <span>Pacing Speed</span>
                        <span className="text-rose-400 font-bold">0.15s / insertion</span>
                      </div>
                      <div className="flex justify-between text-[10px] font-mono text-slate-500">
                        <span>Deduplication</span>
                        <span className="text-amber-500 font-bold">STRICT MATCH</span>
                      </div>
                    </div>

                    {/* Per-Website Statistics Breakdown */}
                    {(() => {
                      const displayStats: Record<string, { scraped: number; duplicates: number }> = Object.keys(statsByWebsite).length > 0 
                        ? statsByWebsite 
                        : {
                            "striptube.cc": { scraped: 0, duplicates: 0 }
                          };

                      return (
                        <div className="pt-3 border-t border-slate-800/60 space-y-2">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block font-mono">
                            Source Sites Progress
                          </span>
                          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                            {Object.entries(displayStats).map(([site, stats]) => (
                              <div key={site} className="bg-slate-950/80 border border-slate-800/65 rounded-xl p-2 space-y-1 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-bold text-slate-200 font-sans truncate pr-2">
                                    {site.toUpperCase()}
                                  </span>
                                  <span className="text-[9px] font-mono text-slate-500 shrink-0">
                                    {stats.scraped + stats.duplicates} scanned
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5 text-[9px] font-mono">
                                  <div className="bg-emerald-500/5 border border-emerald-500/10 rounded px-1.5 py-0.5 flex items-center justify-between text-emerald-400">
                                    <span>scraped:</span>
                                    <span className="font-bold">{stats.scraped}</span>
                                  </div>
                                  <div className="bg-slate-900/40 border border-slate-800/50 rounded px-1.5 py-0.5 flex items-center justify-between text-slate-400">
                                    <span>skipped:</span>
                                    <span className="font-bold">{stats.duplicates}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800/60 mt-4 flex items-center justify-between gap-3">
                  <div className="text-[10px] font-mono text-slate-500 space-y-1">
                    <div>Engine Mode: Recursive Non-blocking Thread</div>
                    <div>Deduplication: STRICT MATCH</div>
                  </div>
                  <button
                    id="dedupe-db-btn"
                    onClick={async () => {
                      showConfirm(
                        "Deduplicate Database?",
                        "This will scan the database and automatically remove all videos with duplicate URLs or titles. Continue?",
                        async () => {
                          try {
                            const savedToken = localStorage.getItem("admin_secret_token") || "";
                            const res = await fetch("/api/admin/dedupe", {
                              method: "POST",
                              headers: { "Authorization": `Bearer ${savedToken}` }
                            });
                            const data = await res.json();
                            const removedCount = data.removed || 0;
                            if (removedCount > 0) {
                              triggerStatus(`Cleanup complete! Auto-deleted ${removedCount} duplicate video(s) across Kama-Baba & web sources.`, "success");
                            } else {
                              triggerStatus(`Database is clean! 0 duplicate videos found.`, "success");
                            }
                            if (onRefreshVideos) onRefreshVideos();
                          } catch (err) {
                            triggerStatus("Failed to deduplicate database.", "error");
                          }
                        },
                        "Yes, Deduplicate"
                      );
                    }}
                    className="px-3 py-1.5 bg-indigo-600/10 hover:bg-indigo-600 text-indigo-400 hover:text-white rounded-lg text-[10px] font-black tracking-widest uppercase transition-all cursor-pointer border border-indigo-500/20 flex items-center gap-2"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    Clean Duplicates
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Crawl Results Listing & Bulk Import */}
          {crawlResults.length > 0 && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-3">
                <div className="space-y-0.5">
                  <h4 className="text-sm sm:text-base font-bold text-slate-200 uppercase tracking-tight font-sans">
                    AI Discovered Streams ({crawlResults.length} Videos)
                  </h4>
                  <p className="text-xs text-slate-400 font-sans">
                    You can inspect, inline edit, select, and import discovered video files or players below.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    id="toggle-select-all-crawl"
                    onClick={handleToggleSelectAll}
                    type="button"
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold cursor-pointer"
                  >
                    {selectedCrawlVideos.length === crawlResults.length ? "Deselect All" : "Select All"}
                  </button>

                  <button
                    id="bulk-import-crawl-btn"
                    onClick={handleImportCrawlVideos}
                    disabled={selectedCrawlVideos.length === 0 || isImporting}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/10"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5" />
                        Publish Selected ({selectedCrawlVideos.length})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Crawl Results Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {crawlResults.map((v, i) => {
                  const isSelected = selectedCrawlVideos.includes(i);
                  return (
                    <div 
                      key={i} 
                      className={`bg-slate-900 border rounded-xl p-4 sm:p-5 relative transition-all duration-300 ${
                        isSelected ? "border-rose-500/50 bg-slate-900/90 shadow-lg shadow-rose-500/5" : "border-slate-800 bg-slate-900/40"
                      }`}
                    >
                      {/* Checkbox Select Toggle */}
                      <div className="absolute top-4 left-4 z-10 flex items-center justify-center">
                        <input
                          type="checkbox"
                          id={`select-scraped-${i}`}
                          checked={isSelected}
                          onChange={() => handleToggleSelectVideo(i)}
                          className="w-5 h-5 accent-rose-600 cursor-pointer rounded border-slate-700"
                        />
                      </div>

                      {/* Video card contents */}
                      <div className="space-y-4 pt-1 pl-7">
                        <div className="flex flex-col sm:flex-row gap-4">
                          {/* Thumbnail preview */}
                          <div className="w-full sm:w-28 h-20 bg-slate-950 border border-slate-800 rounded-lg overflow-hidden relative flex-shrink-0">
                            <img
                              src={getProxiedThumbnailUrl(v.thumbnailUrl, v.title, v.streamUrl)}
                              alt={v.title}
                              referrerPolicy="no-referrer"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                if (img.getAttribute("data-failed")) return;
                                img.setAttribute("data-failed", "true");
                                let fallbackUrl = `/api/proxy?url=${encodeURIComponent(v.thumbnailUrl)}`;
                                if (v.streamUrl) fallbackUrl += `&pageUrl=${encodeURIComponent(v.streamUrl)}`;
                                img.src = fallbackUrl;
                              }}
                            />
                            <div className="absolute bottom-1 right-1 bg-black/80 px-1 py-0.5 rounded text-[9px] font-mono font-bold text-slate-300">
                              {v.duration}
                            </div>
                          </div>

                          {/* Inline editing form inputs */}
                          <div className="flex-1 space-y-2">
                            <div>
                              <input
                                type="text"
                                value={v.title}
                                onChange={(e) => handleEditResultField(i, "title", e.target.value)}
                                className="w-full bg-slate-950 border border-slate-800/80 focus:border-rose-500 rounded px-2.5 py-1.5 text-slate-100 text-xs font-bold focus:outline-none"
                                placeholder="Video title..."
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <select
                                  value={v.category}
                                  onChange={(e) => handleEditResultField(i, "category", e.target.value)}
                                  className="w-full bg-slate-950 border border-slate-800/80 focus:border-rose-500 rounded px-2 py-1 text-slate-300 text-[11px] focus:outline-none"
                                >
                                  <option value="Trending">Trending</option>
                                  <option value="Hot">Hot</option>
                                  <option value="Popular">Popular</option>
                                  <option value="Action">Action</option>
                                  <option value="Drama">Drama</option>
                                  <option value="Nature">Nature</option>
                                  <option value="Adult Content">Adult Content</option>
                                  <option value="General Entertainment">General Entertainment</option>
                                </select>
                              </div>

                              <div>
                                <input
                                  type="text"
                                  value={v.duration}
                                  onChange={(e) => handleEditResultField(i, "duration", e.target.value)}
                                  className="w-full bg-slate-950 border border-slate-800/80 focus:border-rose-500 rounded px-2 py-1 text-slate-300 text-[11px] text-center font-mono focus:outline-none"
                                  placeholder="Duration (e.g. 05:00)"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Description field */}
                        <div>
                          <textarea
                            value={v.description}
                            onChange={(e) => handleEditResultField(i, "description", e.target.value)}
                            rows={2}
                            className="w-full bg-slate-950 border border-slate-800/80 focus:border-rose-500 rounded px-2.5 py-1.5 text-slate-400 text-[11px] focus:outline-none resize-none leading-relaxed"
                            placeholder="Video description..."
                          />
                        </div>

                        {/* Stream URL display & play test */}
                        <div className="flex items-center justify-between text-[10px] font-mono bg-slate-950 px-3 py-2 rounded-lg border border-slate-800/60 text-slate-500">
                          <span className="truncate flex-1 mr-2">{v.videoUrl}</span>
                          <a
                            href={v.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-rose-400 hover:text-rose-300 flex items-center gap-0.5 flex-shrink-0 font-sans"
                          >
                            Test Stream <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 6: USERS & ACTIVE VISITORS MANAGEMENT --- */}
      {activeAdminTab === "visitors" && (
        <div id="visitors-manager-panel" className="space-y-6">
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/60 pb-4">
              <div>
                <h3 className="text-md sm:text-lg font-bold text-slate-200 uppercase tracking-tight flex items-center gap-2">
                  <User className="w-5 h-5 text-rose-500" />
                  Users & Active Visitors Directory
                </h3>
                <p className="text-xs text-slate-400 mt-1 font-sans">
                  Track website traffic, monitor visitor statistics, and block or unblock users instantly.
                </p>
              </div>
              <button
                type="button"
                onClick={fetchVisitors}
                disabled={loadingVisitors}
                className="self-start sm:self-center px-3 py-1.5 bg-slate-950 hover:bg-slate-850 text-slate-300 hover:text-white rounded-lg text-xs font-bold border border-slate-800 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <Loader2 className={`w-3.5 h-3.5 ${loadingVisitors ? "animate-spin text-rose-500" : ""}`} />
                <span>{loadingVisitors ? "Loading Visitors..." : "Refresh Directory"}</span>
              </button>
            </div>

            {/* Filter and Search controls */}
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Search visitors by IP, ID, or browser device..."
                  value={visitorSearch}
                  onChange={(e) => setVisitorSearch(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-850 focus:border-rose-500 rounded-lg pl-9 pr-4 py-2 text-slate-200 text-xs sm:text-sm focus:outline-none placeholder-slate-600 font-sans"
                />
              </div>

              <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-850 self-start">
                {(["all", "active", "blocked"] as const).map((filterOpt) => (
                  <button
                    key={filterOpt}
                    type="button"
                    onClick={() => setVisitorFilter(filterOpt)}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                      visitorFilter === filterOpt
                        ? "bg-rose-600 text-white"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {filterOpt === "all" ? "All" : filterOpt === "active" ? "Active" : "Blocked"}
                  </button>
                ))}
              </div>
            </div>

            {loadingVisitors ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
                <span className="text-[10px] text-rose-500 font-mono tracking-widest uppercase">FETCHING TRAFFIC LOGS...</span>
              </div>
            ) : (() => {
              const filteredVisitors = visitors.filter(v => {
                const matchesSearch = 
                  (v.ip || "").toLowerCase().includes(visitorSearch.toLowerCase()) ||
                  (v.id || "").toLowerCase().includes(visitorSearch.toLowerCase()) ||
                  (v.userAgent || "").toLowerCase().includes(visitorSearch.toLowerCase()) ||
                  (v.lastPage || "").toLowerCase().includes(visitorSearch.toLowerCase());
                
                if (visitorFilter === "blocked") return matchesSearch && v.isBlocked;
                if (visitorFilter === "active") return matchesSearch && !v.isBlocked;
                return matchesSearch;
              });

              if (filteredVisitors.length === 0) {
                return (
                  <div className="text-center py-16 bg-slate-950/40 border border-dashed border-slate-800 rounded-2xl space-y-3">
                    <User className="w-10 h-10 text-slate-600 mx-auto" />
                    <p className="text-xs text-slate-400 font-sans">No matching visitor history logged yet in the database.</p>
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredVisitors.map((visitor) => {
                    const isBlocked = visitor.isBlocked;
                    const dateFirst = visitor.firstVisit ? new Date(visitor.firstVisit) : null;
                    const dateLast = visitor.lastActive ? new Date(visitor.lastActive) : null;
                    
                    return (
                      <div 
                        key={visitor.id} 
                        className={`p-4 rounded-xl border transition-all flex flex-col justify-between gap-4 ${
                          isBlocked 
                            ? "bg-rose-950/5 border-rose-500/10 hover:border-rose-500/30" 
                            : "bg-slate-950/40 border-slate-850 hover:border-slate-800"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1.5 flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-black text-slate-200 truncate font-mono tracking-wider bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                                ID: {visitor.id.replace("visit_", "").substring(0, 10)}...
                              </span>
                              {isBlocked ? (
                                <span className="px-1.5 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded text-[9px] font-black uppercase tracking-wider flex items-center gap-1">
                                  <ShieldAlert className="w-2.5 h-2.5" />
                                  Suspended
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[9px] font-black uppercase tracking-wider">
                                  Active
                                </span>
                              )}
                            </div>
                            
                            <div className="space-y-1 text-slate-400 text-xs font-sans">
                              <p className="flex items-center gap-1.5 text-[11px] font-medium text-slate-300">
                                <span className="text-slate-500 font-mono">IP:</span>
                                <span className="font-mono bg-slate-900/40 px-1 py-0.5 rounded border border-slate-800/40">{visitor.ip || "Unknown IP"}</span>
                              </p>
                              <p className="truncate text-[10px] text-slate-400" title={visitor.userAgent}>
                                <span className="text-slate-500 font-mono">UA:</span> {visitor.userAgent}
                              </p>
                              <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-500 pt-1">
                                <div>
                                  <span className="block text-[8px] text-slate-600 font-mono uppercase">First Session</span>
                                  <span className="text-slate-400">{dateFirst ? dateFirst.toLocaleTimeString() : "N/A"}</span>
                                </div>
                                <div>
                                  <span className="block text-[8px] text-slate-600 font-mono uppercase">Last Request</span>
                                  <span className="text-rose-400 font-bold">{dateLast ? dateLast.toLocaleTimeString() : "N/A"}</span>
                                </div>
                              </div>
                              <p className="text-[10px] text-slate-400 font-sans truncate bg-slate-900/20 px-2 py-1 rounded border border-slate-850 mt-1">
                                <span className="text-slate-500 font-mono mr-1">Path:</span>
                                <span className="text-indigo-400 font-mono">{visitor.lastPage || "/"}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end justify-between h-full min-h-[90px]">
                            <div className="text-right">
                              <span className="block text-[8px] text-slate-500 font-mono uppercase tracking-wider">Total Clicks</span>
                              <span className="text-md sm:text-lg font-black text-slate-100 font-mono">{visitor.totalRequests || 1}</span>
                            </div>

                            <div className="flex items-center gap-1.5 pt-4">
                              {isBlocked ? (
                                <button
                                  type="button"
                                  onClick={() => handleUnblockVisitor(visitor.id, visitor.ip)}
                                  className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all min-h-[30px] flex items-center justify-center border border-emerald-500/20"
                                >
                                  Unblock
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleBlockVisitor(visitor.id, visitor.ip)}
                                  className="px-2.5 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all min-h-[30px] flex items-center justify-center border border-rose-500/20 shadow-md shadow-rose-600/10"
                                >
                                  Block
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteVisitor(visitor.id)}
                                className="px-2.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-800 rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all min-h-[30px] flex items-center justify-center"
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}



      {/* Custom Confirmation Modal Overlay */}
      {confirmModal.isOpen && (
        <div id="custom-confirm-modal" className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl shadow-rose-950/40 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-500 flex-shrink-0">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-black text-slate-100 font-sans tracking-tight">
                  {confirmModal.title}
                </h4>
                <p className="text-xs text-slate-400 leading-relaxed mt-1 font-sans">
                  {confirmModal.message}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 bg-slate-950 hover:bg-slate-850 text-slate-400 hover:text-slate-200 border border-slate-800 rounded-lg text-xs font-bold transition-all cursor-pointer min-h-[44px] flex items-center justify-center min-w-[80px]"
              >
                {confirmModal.cancelText}
              </button>
              <button
                type="button"
                onClick={() => {
                  confirmModal.onConfirm();
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-rose-600/20 cursor-pointer min-h-[44px] flex items-center justify-center min-w-[120px]"
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
